import apiClient from './client';
import type { PaginatedResponse } from '@/types/api';
import type { Family, FamilyFormData, FamilyFilters, Visit, VisitFormData } from '@/types/family';

/** Unwrap Laravel's { data: T } envelope for single-resource responses */
function unwrap<T>(data: T | { data: T }): T {
  if (data && typeof data === 'object' && 'data' in data && !Array.isArray((data as { data: T }).data)) {
    return (data as { data: T }).data;
  }
  return data as T;
}

/** Compute derived fields from nested API data */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeFamily(raw: any): Family {
  const f = raw as Family;
  if (!f.sector_name && raw.sector?.name) f.sector_name = raw.sector.name;
  // member_count: prefer backend-computed members_count, fallback to array length
  if (f.member_count == null) f.member_count = raw.members_count ?? (Array.isArray(raw.members) ? raw.members.length : 0);
  // display_name: prefer backend-computed, fallback to name
  if (!f.display_name) f.display_name = f.name;
  // Normalize members only when present (detail view)
  if (Array.isArray(f.members) && f.members.length > 0 && f.members[0]?.last_name) {
    f.members = f.members.map((m: any) => ({
      ...m,
      full_name: m.full_name || `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim(),
      age: m.age ?? (m.birth_date ? Math.floor((Date.now() - new Date(m.birth_date).getTime()) / 31557600000) : null),
    }));
  } else if (!Array.isArray(f.members)) {
    f.members = [];
  }
  return f;
}

export async function getFamilies(
  params?: FamilyFilters,
): Promise<PaginatedResponse<Family>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = await apiClient.get<any>('/families', { params });
  const raw = response.data;

  // Laravel Resource::collection wraps pagination in { data, meta, links }
  // Flatten meta into top-level to match PaginatedResponse<T>
  const meta = raw.meta ?? {};
  const result: PaginatedResponse<Family> = {
    data: (raw.data ?? []).map(normalizeFamily),
    current_page: meta.current_page ?? raw.current_page ?? 1,
    last_page: meta.last_page ?? raw.last_page ?? 1,
    per_page: meta.per_page ?? raw.per_page ?? 25,
    total: meta.total ?? raw.total ?? 0,
    from: meta.from ?? raw.from ?? null,
    to: meta.to ?? raw.to ?? null,
    next_page_url: raw.links?.next ?? meta.next_page_url ?? raw.next_page_url ?? null,
    prev_page_url: raw.links?.prev ?? meta.prev_page_url ?? raw.prev_page_url ?? null,
  };
  return result;
}

export async function getFamily(id: string): Promise<Family> {
  const response = await apiClient.get(`/families/${id}`);
  return normalizeFamily(unwrap(response.data));
}

export async function createFamily(data: FamilyFormData): Promise<Family> {
  const response = await apiClient.post('/families', data);
  return unwrap<Family>(response.data);
}

export async function updateFamily(
  id: string,
  data: Partial<FamilyFormData>,
): Promise<Family> {
  const response = await apiClient.put(`/families/${id}`, data);
  return unwrap<Family>(response.data);
}

export async function deleteFamily(id: string): Promise<void> {
  await apiClient.delete(`/families/${id}`);
}

export interface BlockMatch {
  id: string;
  name: string;
  address: string | null;
  numar: string | null;
  bloc: string | null;
  scara: string | null;
  etaj: string | null;
  apartament: string | null;
  city: string | null;
  latitude: number;
  longitude: number;
}

export async function matchBlock(bloc: string, address?: string): Promise<BlockMatch[]> {
  const params: Record<string, string> = { bloc };
  if (address) params.address = address;
  const response = await apiClient.get('/families/match-block', { params });
  return (response.data as { data: BlockMatch[] }).data ?? [];
}

export async function getFamilyVisits(id: string): Promise<Visit[]> {
  const response = await apiClient.get(`/families/${id}/visits`);
  const result = response.data;
  return Array.isArray(result) ? result : (result as { data: Visit[] }).data ?? result;
}

export async function createVisit(data: VisitFormData): Promise<Visit> {
  const response = await apiClient.post('/visits', data);
  return unwrap<Visit>(response.data);
}
